import { Checkbox, TableBody, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import { Dayjs } from 'dayjs'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetWorkHoursSummaryQuery } from '../../../store/redux/reducers/worklogSlice'
import DateFormat from '../../../store/types/enums/DateFormat'
import HoursSummaryEmployee from '../../../store/types/models/HoursSummaryEmployee'
import { getDaysOfTheMonth } from '../../../store/utils/worklogUtils'
import {
  stickyStyles,
  StyledEmployeeMonthlyHoursHeaderCell,
  StyledEmployeeMonthlyHoursTable,
} from '../../../styles/EmployeeMonthlyHoursTableStyles'
import Loading from '../../Loading/Loading'
import EmployeeMonthlyHoursFooter from './EmployeeMonthlyHoursFooter'
import EmployeeMonthlyHoursTableRow from './EmployeeMonthlyHoursTableRow'

type EmployeeMonthlyHoursTableProps = {
  selectedDate: Dayjs
  selectedEmployees: HoursSummaryEmployee[]
  setSelectedEmployees: React.Dispatch<React.SetStateAction<HoursSummaryEmployee[]>>
  enabled?: boolean
}

const EmployeeMonthlyHoursTable = ({
  selectedDate,
  selectedEmployees,
  setSelectedEmployees,
  enabled = true,
}: EmployeeMonthlyHoursTableProps) => {
  const { t } = useTranslation()
  const daysOfTheMonth = useMemo(() => {
    return getDaysOfTheMonth(selectedDate)
  }, [selectedDate])

  const { data, isLoading } = useGetWorkHoursSummaryQuery(
    { date: selectedDate.format(DateFormat.YearMonth) },
    { skip: !enabled }
  )

  const handleToggleAllEmployees = () => {
    setSelectedEmployees(prev => {
      if (prev.length === data?.employees.length) {
        return []
      } else {
        return data
          ? data.employees.map(e => ({
              id: e.employee.id,
              name: `${e.employee.firstName} ${e.employee.lastName}`,
            }))
          : []
      }
    })
  }

  const handleToggleEmployee = (employeeId: string, employeeName: string) => {
    setSelectedEmployees(prev => {
      if (prev.some(e => e.id === employeeId)) {
        return prev.filter(e => e.id !== employeeId)
      } else {
        return [...prev, { id: employeeId, name: employeeName }]
      }
    })
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <TableContainer sx={{ maxHeight: '75vh' }}>
      <StyledEmployeeMonthlyHoursTable>
        <TableHead sx={{ ...stickyStyles, top: 0, left: 0, zIndex: 11 }}>
          <TableRow>
            <StyledEmployeeMonthlyHoursHeaderCell
              sx={{ ...stickyStyles, top: 0, left: 0, p: 0, borderBottom: 'none' }}
              align="center"
            >
              <Checkbox
                sx={{ width: 'fit-content' }}
                size="small"
                checked={selectedEmployees.length === data?.employees.length}
                onChange={handleToggleAllEmployees}
              />
            </StyledEmployeeMonthlyHoursHeaderCell>
            {data?.employees.map(employeeData => {
              const employee = employeeData.employee
              const isChecked = selectedEmployees.some(e => e.id === employee.id)

              return (
                <StyledEmployeeMonthlyHoursHeaderCell
                  key={employee.id}
                  sx={{ p: 0, borderBottom: 'none' }}
                  align="center"
                >
                  <Checkbox
                    sx={{ width: 'fit-content' }}
                    size="small"
                    checked={isChecked}
                    onChange={() => handleToggleEmployee(employee.id, `${employee.firstName} ${employee.lastName}`)}
                  />
                </StyledEmployeeMonthlyHoursHeaderCell>
              )
            })}
          </TableRow>
          <TableRow>
            <StyledEmployeeMonthlyHoursHeaderCell sx={{ ...stickyStyles, top: 0, left: 0, p: 0.5 }} align="center">
              <Typography variant="body2" fontWeight={500}>
                {t('selectAll')}
              </Typography>
            </StyledEmployeeMonthlyHoursHeaderCell>
            {data?.employees.map(employeeData => {
              const employee = employeeData.employee

              return (
                <StyledEmployeeMonthlyHoursHeaderCell
                  key={employee.id}
                  sx={{ p: 0.5, maxHeight: '60px' }}
                  align="center"
                >
                  <Tooltip title={`${employee.firstName} ${employee.lastName}`}>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-word',
                      }}
                    >
                      {employee.firstName} {employee.lastName}
                    </Typography>
                  </Tooltip>
                </StyledEmployeeMonthlyHoursHeaderCell>
              )
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {daysOfTheMonth.map(day => (
            <EmployeeMonthlyHoursTableRow key={day.id} day={day} employeesMonthlySummary={data ? data.employees : []} />
          ))}
        </TableBody>
        <EmployeeMonthlyHoursFooter workHoursMonthlySummary={data} />
      </StyledEmployeeMonthlyHoursTable>
    </TableContainer>
  )
}

export default EmployeeMonthlyHoursTable
